import { useSearchParams } from "react-router"

/**
 * Global insights display-window selection, persisted in the URL search params
 * so it is shareable + survives reload. Read/written by the `/neat` window
 * selector and threaded into every insight fetch (window / start / end).
 *
 * A `trailing_12mo` default is represented as the ABSENCE of a `window` param
 * (clean default URL). A custom range is `start` + `end` as `YYYY-MM`.
 */

const PRESET_LABELS: Record<string, string> = {
  trailing_12mo: "Trailing 12 mo",
  trailing_6mo: "Trailing 6 mo",
  trailing_3mo: "Trailing 3 mo",
  ytd: "Year to date",
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

const monthLabel = (ym: string): string | null => {
  const [y, m] = ym.split("-")
  const mi = Number(m)
  if (!y || !(mi >= 1 && mi <= 12)) {
    return null
  }
  return `${MONTHS[mi - 1]} ${y}`
}

export interface InsightsWindow {
  window?: string
  start?: string
  end?: string
  label: string
  setPreset: (w: string) => void
  setCustom: (start: string, end: string) => void
}

export const useInsightsWindow = (): InsightsWindow => {
  const [params, setParams] = useSearchParams()
  const window = params.get("window") ?? undefined
  const start = params.get("start") ?? undefined
  const end = params.get("end") ?? undefined

  let customLabel: string | null = null
  if (start != null && end != null) {
    const s = monthLabel(start)
    const e = monthLabel(end)
    customLabel = s != null && e != null ? `${s} – ${e}` : null
  }

  const label =
    customLabel ?? PRESET_LABELS[window ?? "trailing_12mo"] ?? "Trailing 12 mo"

  const setPreset = (w: string): void => {
    const next = new URLSearchParams(params)
    next.delete("start")
    next.delete("end")
    if (w === "trailing_12mo") {
      next.delete("window")
    } else {
      next.set("window", w)
    }
    setParams(next, { replace: true })
  }

  const setCustom = (s: string, e: string): void => {
    const next = new URLSearchParams(params)
    next.delete("window")
    next.set("start", s)
    next.set("end", e)
    setParams(next, { replace: true })
  }

  return { window, start, end, label, setPreset, setCustom }
}
